import { Slide } from '@features/powerpoint';
import { dimensionSlide } from '@features/executive-report/slides/v2/dimension';
import { buildNavbarText } from '@features/executive-report/slides/v2/components/navbar-texts';
import { navbarSections } from '../../domain/navbar-sections';
import { themeV2 } from '@features/executive-report/domain/theme-v2';
import { formatters } from '@utils/formattersV2';
import { formatWithSign } from '@features/executive-report/utils/format-with-sign';
import { visibleSlide } from '@features/executive-report/slides';
import { FormSchema } from '../../form-schema';
import { BuildReportOptionsV2 } from '../../utils/build-slides-v2';

type BuildDimensionSlideProps = {
  navbarSection: (typeof navbarSections)[number];
  dimensionName: string;
  dimensionSlug: string;
  headerPrefix: string;
  dimensions: NonNullable<
    BuildReportOptionsV2['topVisitSubdimensionsByName']['ad']
  >;
  overallMetrics: Parameters<typeof dimensionSlide>[0]['overallMetrics'];
  subdimensionImages: FormSchema['subdimensionImagesByNameAndValue'];
};

const buildDimensionSlide = (props: BuildDimensionSlideProps): Slide => {
  if (props.dimensions.length === 0) return {};

  return dimensionSlide({
    navbarText: buildNavbarText(navbarSections, props.navbarSection),
    dimensionName: props.dimensionName,
    overallMetrics: props.overallMetrics,
    dimensionMetrics: props.dimensions.map(
      (
        subdimension
      ): Parameters<typeof dimensionSlide>[0]['dimensionMetrics'][number] => ({
        dimension: subdimension.value,
        image:
          props.subdimensionImages[props.dimensionSlug]?.[subdimension.value] ??
          null,
        metrics: [
          {
            label: 'Visit Lift',
            value: formatWithSign(
              subdimension.visitMetrics.incrementalLift,
              value => formatters.lift(value, { decimals: 1 })
            ),
          },
          {
            label: 'Incremental Visits',
            value: formatters.visits(
              subdimension.visitMetrics.incrementalVisits,
              { full: true }
            ),
          },
          {
            label: 'Impressions',
            value: formatters.impressions(
              subdimension.visitMetrics.impressions,
              { full: true }
            ),
          },
        ],
      })
    ),
    headerText: {
      text: [
        {
          text: `${props.headerPrefix}: `,
          options: {
            color: themeV2.colors.inmarketGreen,
          },
        },
        {
          text: `${props.dimensionName}s`,
        },
      ],
      options: {
        fontSize: 36,
        bold: true,
        h: 0.75,
        w: 12.67,
        x: 0.33,
        y: 0.59,
      },
    },
  });
};

export const visitDimensionsSlides = (props: BuildReportOptionsV2): Slide[] => {
  const overallMetrics: Parameters<typeof dimensionSlide>[0]['overallMetrics'] =
    [
      {
        label: 'Incremental Visits and Lift',
        value: formatters.visits(
          props.currentReport.visitMetrics.incrementalVisits
        ),
        lift: formatWithSign(
          props.currentReport.visitMetrics.incrementalLift,
          value => formatters.lift(value, { decimals: 1 })
        ),
      },
      {
        label: 'Total Projected Visits',
        value: formatters.visits(
          props.currentReport.visitMetrics.projectedVisits,
          { full: true }
        ),
      },
      {
        label: 'Unique Users',
        value: formatters.users(props.currentReport.visitMetrics.uniqueUsers, {
          full: true,
        }),
      },
      {
        label: 'Total Impressions',
        value: formatters.impressions(
          props.currentReport.visitMetrics.impressions,
          { full: true }
        ),
      },
    ];

  return [
    ...visibleSlide({
      slide: buildDimensionSlide({
        navbarSection: 'The Who',
        dimensionName: 'Audience',
        dimensionSlug: 'aud',
        headerPrefix: 'The WHO',
        dimensions: props.topVisitSubdimensionsByName.aud ?? [],
        overallMetrics,
        subdimensionImages: props.customFields.subdimensionImagesByNameAndValue,
      }),
      visible:
        props.customFields.includedSlides.visitLift.who &&
        (props.topVisitSubdimensionsByName.aud?.length ?? 0) > 0,
    }),
    ...visibleSlide({
      slide: buildDimensionSlide({
        navbarSection: 'The What',
        dimensionName: 'Creative',
        dimensionSlug: 'creative',
        headerPrefix: 'The WHAT',
        dimensions: props.topVisitSubdimensionsByName.creative ?? [],
        overallMetrics,
        subdimensionImages: props.customFields.subdimensionImagesByNameAndValue,
      }),
      visible:
        props.customFields.includedSlides.visitLift.what &&
        (props.topVisitSubdimensionsByName.creative?.length ?? 0) > 0,
    }),
    ...visibleSlide({
      slide: buildDimensionSlide({
        navbarSection: 'The Where',
        dimensionName: 'Channel',
        dimensionSlug: 'channel',
        headerPrefix: 'The WHERE',
        dimensions: props.topVisitSubdimensionsByName.channel ?? [],
        overallMetrics,
        subdimensionImages: props.customFields.subdimensionImagesByNameAndValue,
      }),
      visible:
        props.customFields.includedSlides.visitLift.where &&
        (props.topVisitSubdimensionsByName.channel?.length ?? 0) > 0,
    }),
  ];
};
